import { createGeminiImage, fileToBase64, isGeminiAvailable } from './gemini-client.js';

const form = document.querySelector('[data-retouch-form]');
const fileInput = document.querySelector('#retouch-photo');
const fileLabel = document.querySelector('[data-file-label]');
const statusEl = document.querySelector('[data-status]');
const photoHelper = document.querySelector('[data-photo-helper]');
const styleHelper = document.querySelector('[data-style-helper]');
const styleRadios = Array.from(document.querySelectorAll('input[name="retouch-style"]'));
const uploadPreview = document.querySelector('[data-upload-preview]');
const resultPreview = document.querySelector('[data-result-preview]');
const downloadBtn = document.querySelector('[data-download]');
const submitBtn = document.querySelector('[data-retouch-submit]');
const resetBtn = document.querySelector('[data-reset]');

const MAX_FILE_SIZE = 6 * 1024 * 1024;

const STYLE_PROMPTS = {
  indoor:
    'Apply a warm indoor glow with soft window light, flattering shadows, and gentle color balance while reducing noise.',
  outdoor:
    'Enhance the sense of an open meadow: fresh greens, airy highlights, crisp whiskers, and subtle sunlit contrast.',
  cozy:
    'Create a boutique studio mood with muted backdrops, spotlight warmth, and editorial contrast while keeping the rabbit relaxed.',
  natural:
    'Keep tones documentary-true with accurate whites, mild film grain, and refined clarity that still feels candid.'
};

const DEFAULT_PHOTO_HELPER =
  'High-resolution portraits give the best finish. Photos only leave your browser during retouching and are discarded afterward.';
const DEFAULT_STYLE_HELPER = 'Select one ambiance to unlock retouching.';

const state = {
  file: null,
  style: ''
};

let geminiReady = true;
let uploadObjectUrl = '';

function setStatus(message, isError = false) {
  if (!statusEl) return;
  statusEl.textContent = message;
  statusEl.classList.toggle('error', isError);
}

function updateSubmitState() {
  const ready = geminiReady && state.file && state.style;
  if (submitBtn) {
    submitBtn.disabled = !ready;
  }
}

function updateFileHelper(isError, message) {
  if (!photoHelper) return;
  photoHelper.textContent = message;
  photoHelper.classList.toggle('error', isError);
}

function updateStyleHelper(isError, message) {
  if (!styleHelper) return;
  styleHelper.textContent = message;
  styleHelper.classList.toggle('error', isError);
}

function resetPreview(target, placeholderText) {
  if (!target) return;
  target.style.backgroundImage = '';
  target.classList.add('placeholder');
  if (placeholderText) {
    target.setAttribute('data-placeholder', placeholderText);
  }
}

function setPreview(target, imageUrl) {
  if (!target) return;
  target.style.backgroundImage = imageUrl ? `url(${imageUrl})` : '';
  target.classList.toggle('placeholder', !imageUrl);
}

function updateUploadPreview(file) {
  if (uploadObjectUrl) {
    URL.revokeObjectURL(uploadObjectUrl);
    uploadObjectUrl = '';
  }
  if (!file) {
    resetPreview(uploadPreview);
    return;
  }
  uploadObjectUrl = URL.createObjectURL(file);
  setPreview(uploadPreview, uploadObjectUrl);
}

function clearResultPreview() {
  resetPreview(resultPreview);
  if (downloadBtn) {
    downloadBtn.classList.add('is-hidden');
    downloadBtn.href = '#';
  }
}

async function initAvailability() {
  if (!form) return;
  geminiReady = await isGeminiAvailable();
  if (!geminiReady) {
    setStatus('Image services are offline. Browse presets while we restore the atelier.');
  }
  updateSubmitState();
}

function handleFileChange(event) {
  const file = event.target.files?.[0];
  if (fileLabel) {
    fileLabel.textContent = file ? file.name : 'No file selected';
  }
  if (file && file.size > MAX_FILE_SIZE) {
    updateFileHelper(true, 'Image exceeds 6MB. Please compress before uploading.');
    event.target.value = '';
    state.file = null;
    updateUploadPreview(null);
    clearResultPreview();
    updateSubmitState();
    return;
  }
  updateFileHelper(false, DEFAULT_PHOTO_HELPER);
  state.file = file || null;
  updateUploadPreview(state.file);
  clearResultPreview();
  updateSubmitState();
}

function handleStyleChange(event) {
  state.style = event.target.value;
  updateStyleHelper(false, 'Style locked in. Adjust anytime before retouching.');
  clearResultPreview();
  updateSubmitState();
}

async function handleSubmit(event) {
  event.preventDefault();
  if (!geminiReady) {
    setStatus('Image services are offline. Please try again soon.', true);
    return;
  }
  if (!state.file) {
    setStatus('Please upload a rabbit portrait first.', true);
    updateFileHelper(true, 'Upload a PNG or JPG to continue.');
    return;
  }
  if (!state.style) {
    setStatus('Choose a retouch vibe to continue.', true);
    updateStyleHelper(true, 'Pick one of the listed ambiences.');
    return;
  }

  const stylePrompt = STYLE_PROMPTS[state.style];
  if (!stylePrompt) {
    setStatus('Selected style is unavailable.', true);
    return;
  }

  try {
    setStatus('Retouching in progress—please wait a few moments.');
    clearResultPreview();
    submitBtn?.setAttribute('disabled', 'true');
    const { base64, mimeType } = await fileToBase64(state.file);
    const prompt = `${stylePrompt} Maintain the rabbit's natural fur texture, markings, and proportions. Remove distractions and deliver a polished yet believable portrait.`;
    const outputBase64 = await createGeminiImage({
      prompt,
      base64Image: base64,
      mimeType
    });
    const dataUrl = `data:image/png;base64,${outputBase64}`;
    setPreview(resultPreview, dataUrl);
    if (downloadBtn) {
      downloadBtn.href = dataUrl;
      downloadBtn.download = 'croissant-retouch.png';
      downloadBtn.classList.remove('is-hidden');
    }
    setStatus('Retouch complete! Save the PNG or reset to try another vibe.');
  } catch (error) {
    console.error(error);
    setStatus(error.message || 'Retouch failed. Please try again.', true);
    clearResultPreview();
  } finally {
    updateSubmitState();
  }
}

function resetTool() {
  state.file = null;
  state.style = '';
  if (fileInput) {
    fileInput.value = '';
  }
  if (fileLabel) {
    fileLabel.textContent = 'No file selected';
  }
  styleRadios.forEach((radio) => {
    radio.checked = false;
  });
  updateUploadPreview(null);
  clearResultPreview();
  updateFileHelper(false, DEFAULT_PHOTO_HELPER);
  updateStyleHelper(false, DEFAULT_STYLE_HELPER);
  setStatus('Upload a photo and choose a vibe to begin.');
  updateSubmitState();
}

function registerEvents() {
  if (!form) return;
  fileInput?.addEventListener('change', handleFileChange);
  styleRadios.forEach((radio) => {
    radio.addEventListener('change', handleStyleChange);
  });
  form.addEventListener('submit', handleSubmit);
  resetBtn?.addEventListener('click', resetTool);
}

registerEvents();
initAvailability();
